import { ScrollToBottomButton } from '@modules/common/components/ScrollToBottomButton';
import { AskOracleInput } from '@modules/oracle/components/AskOracleInput';
import { CreateCopyChatButton } from '@modules/oracle/components/WisdomChatConversations/CreateCopyChatButton';
import { WisdomChatToolbar } from '@modules/oracle/components/WisdomChatToolbar';
import {
  useGetPromptPosition,
  useHasChatEditAccess,
  useStopAssistantResponse,
} from '@modules/oracle/hooks';
import { OracleSearchActionType } from '@modules/oracle/store/oracleActionType';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { OraclePrompt } from '@modules/oracle/store/types';
import { trackWisdomEvents } from '@modules/oracle/utils/trackWisdomEvents';
import { Box, Divider, Stack } from '@mui/material';
import { cloneDeep, throttle } from 'lodash';
import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { OracleSearchConversation } from './OracleSearchConversation';

export function WisdomChatConversations() {
  const sessionID = useOracleSearchStore(({ data }) => data.sessionID);
  const currentPromptID = useOracleSearchStore(
    ({ data }) => data.currentPromptID,
  );
  const promptByID = useOracleSearchStore(({ data }) => data.promptByID);
  const dispatch = useOracleSearchStore(({ dispatch }) => dispatch);
  const [stopAssistantResponse] = useStopAssistantResponse();
  const conversationsContainerRef = useRef<HTMLDivElement | null>(null);
  const hasChatEditAccess = useHasChatEditAccess();
  const [shouldAutoScroll, setShouldAutoScroll] = useState(hasChatEditAccess);
  const [shouldShowScrollToBottomButton, setShouldShowScrollToBottomButton] =
    useState(false);
  const threadPosition = useGetPromptPosition(currentPromptID);

  const isScrollAtBottom = () => {
    const container = conversationsContainerRef.current;

    if (!container) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;
    const deltaOffset = 20;

    return scrollTop + clientHeight + deltaOffset >= scrollHeight;
  };

  const handleScroll = useMemo(
    () =>
      throttle(() => {
        setShouldShowScrollToBottomButton(!isScrollAtBottom());
      }, 250),
    [],
  );

  const scrollToBottom = useCallback(() => {
    const container = conversationsContainerRef.current;

    if (!container) {
      return;
    }

    container.lastElementChild?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });

    setShouldShowScrollToBottomButton(false);
  }, []);

  const stopGeneratingResponse = () => {
    if (!currentPromptID) {
      return;
    }

    const updatedPromptByID = cloneDeep(promptByID ?? {});

    if (updatedPromptByID) {
      updatedPromptByID[currentPromptID].isResponseHalted = true;
      updatedPromptByID[currentPromptID].endOfResponse = true;
    }

    dispatch({
      type: OracleSearchActionType.SET_DATA,
      payload: {
        promptByID: updatedPromptByID,
      },
    });

    stopAssistantResponse(currentPromptID);

    trackWisdomEvents({
      eventName: 'Wisdom - Stop Generating Response Clicked',
      sessionID,
      threadPosition,
      searchText: updatedPromptByID[currentPromptID]?.question,
    });
  };

  const renderPrompts = () => {
    if (!currentPromptID) {
      return null;
    }

    const promptElements = [];
    const hasMultiplePrompts =
      !!promptByID?.[currentPromptID].previousResponseID;
    let promptID: string | undefined = currentPromptID;

    while (promptID) {
      const prompt: OraclePrompt | undefined = promptByID?.[promptID];

      if (!prompt) {
        return null;
      }

      const isLastPrompt = prompt.id === currentPromptID;

      const getIsReadOnly = () => {
        if (!hasChatEditAccess) {
          return true;
        }

        return hasMultiplePrompts ? !isLastPrompt : false;
      };

      const promptElement = (
        <Fragment key={prompt.id}>
          <Box>
            <OracleSearchConversation
              prompt={prompt}
              onContentLoaded={() => {
                if (shouldAutoScroll) {
                  scrollToBottom();
                } else {
                  handleScroll();
                }
              }}
              isReadOnly={getIsReadOnly()}
            />
          </Box>
          {hasMultiplePrompts && !isLastPrompt ? <Divider /> : null}
        </Fragment>
      );

      promptElements.unshift(promptElement);
      promptID = prompt?.previousResponseID;
    }

    return promptElements;
  };

  const scrollToBottomCTA = shouldShowScrollToBottomButton ? (
    <ScrollToBottomButton
      onClick={() => {
        setShouldAutoScroll(true);
      }}
      containerRef={conversationsContainerRef}
    />
  ) : null;

  const currentPrompt = currentPromptID
    ? promptByID?.[currentPromptID]
    : undefined;
  const isPreviousPromptResponsePending = currentPrompt
    ? !currentPrompt?.endOfResponse
    : false;
  const isPreviousPromptErrored = currentPrompt ? !!currentPrompt.error : false;

  return (
    <Stack sx={{ height: '100%', width: '100%' }}>
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {scrollToBottomCTA}
        <Box
          sx={{ height: '100%', width: '100%', overflowY: 'auto' }}
          ref={conversationsContainerRef}
          onWheel={() => {
            setShouldAutoScroll(false);
            handleScroll();
          }}
          onTouchStart={() => {
            setShouldAutoScroll(false);
            handleScroll();
          }}
        >
          <WisdomChatToolbar />
          <Stack
            direction="column"
            spacing={3}
            sx={{
              pb: 4.5,
              maxWidth: '1024px',
              mx: 'auto',
              px: 6,
              boxSizing: 'content-box',
            }}
          >
            {renderPrompts()}
          </Stack>
        </Box>
      </Box>
      {hasChatEditAccess ? (
        <AskOracleInput
          containerSxProps={{ pb: 1, px: 6 }}
          shouldBlockSubmit={
            isPreviousPromptErrored ? false : isPreviousPromptResponsePending
          }
          onSubmit={(searchText: string) => {
            setShouldAutoScroll(true);
            trackWisdomEvents({
              eventName: 'Wisdom - Input Submitted',
              searchText,
              sessionID,
              threadPosition,
            });
          }}
          shouldShowStopButton={
            isPreviousPromptErrored ? false : isPreviousPromptResponsePending
          }
          onStop={stopGeneratingResponse}
        />
      ) : (
        <Stack direction="row" justifyContent="center" sx={{ my: 2 }}>
          <CreateCopyChatButton />
        </Stack>
      )}
    </Stack>
  );
}
